<template>
  <div class="app-tiles-container gs-funnels-page">
    <div class="gs-funnels-page__content">
      <v-wait for="funnels">
        <div v-if="loadError" class="gs-funnels-page__state">
          <h1>We couldn't load your funnels</h1>
          <p>{{ loadError }}</p>
          <gd-button variant="primary" @click="retryFunnels">Try again</gd-button>
        </div>

        <funnels-assets-list
          v-if="!loadError"
          :funnels="funnels"
          :selected-order="selectedOrder"
          :products="products"
          @updateFunnels="funnels = $event"
          @updateSelectedOrder="selectedOrder = $event"
          @onManageProducts="products"
          @onCloneFunnels="openCloneFunnelModel"
          @onEditFunnel="openEditFunnelModel"
          @onPublishFunnel="openPublicationSettings"
        />

        <funnel-settings-drawer
          v-if="funnel"
          :funnel="funnel"
          :open-publication="openPublication"
          @saved="funnelUpdated"
          @close="funnel = null"
        />

        <clone-funnel-modal
          v-if="originalFunnel"
          :original-funnel="originalFunnel"
        />

        <portal to="headerContent">
          <gd-button v-if="!loadError" variant="primary" class="mr-4 gs-funnels-page__create" @click="openCreateFunnel">{{
            $t('frontend_funnels_new_product')
          }}</gd-button>
        </portal>
      </v-wait>
      <gs-toast-provider></gs-toast-provider>
    </div>
  </div>
</template>
<script>
import Funnel from '@/models/Funnel';
import FunnelsAssetsList from './index-partials/FunnelsAssetsList';
import CloneFunnelModal from './index-partials/CloneFunnelModal.vue';
import FunnelSettingsDrawer from './index-partials/FunnelSettingsDrawer.vue';

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('my_product_funnels') };
  },
  components: {
    FunnelsAssetsList,
    CloneFunnelModal,
    FunnelSettingsDrawer,
  },
  data() {
    return {
      selectedOrder: 'created_at',
      funnel: null,
      openPublication: false,
      originalFunnel: null,
      funnels: [],
      loadError: '',
    };
  },
  computed: {
    admin() {
      return this.$store.state.user.admin;
    },
  },
  async created() {
    this.$wait.start('funnels');
    try {
      this.funnels = await Funnel.orderBy(this.selectedOrder).$get();
      if (this.$route.query.new && this.$route.query.new == 'modal') {
        this.openCreateFunnel();
      }
    } catch (error) {
      this.loadError = error?.response?.data?.message ||
        'GrooveSell could not retrieve your funnels. Please try again.';
    } finally {
      this.$wait.end('funnels');
    }
  },
  methods: {
    retryFunnels() {
      window.location.reload();
    },
    products(funnel_id) {
      this.$router.push({ name: 'products', params: { id: funnel_id } });
    },
    openCreateFunnel() {
      this.openPublication = false;
      this.funnel = new Funnel({});
    },
    openPublicationSettings(funnel_id) {
      this.openEditFunnelModel(funnel_id);
      this.openPublication = true;
    },
    openEditFunnelModel(funnel_id) {
      this.openPublication = false;
      const funnelIndex = this.funnels.map((item) => item.id).indexOf(funnel_id);

      this.funnel = this.funnels[funnelIndex];
    },
    openCloneFunnelModel(funnel_id) {
      const cloneFunnelId = this.funnels.map((item) => item.id).indexOf(funnel_id);

      this.originalFunnel = this.funnels[cloneFunnelId];
    },
    funnelUpdated(funnel) {
      this.$gsToast.success('Funnel settings saved.');
      if(!funnel.id) {
        this.funnel = null;
        return;
      }

      const edited_funnel = this.funnels.map((item) => item.id).indexOf(funnel.id);

      if (edited_funnel === -1) {
        this.funnels.unshift(funnel);
        this.funnel = null;
        return;
      }

      this.funnels[edited_funnel].internal_name = funnel.internal_name;
      this.funnels[edited_funnel].name = funnel.name;
      this.funnels[edited_funnel].description = funnel.description;
      this.funnels[edited_funnel].category = funnel.category;
      this.funnels[edited_funnel].slug = funnel.slug;
      this.funnels[edited_funnel].is_live = funnel.is_live;
      this.funnels[edited_funnel].sender_id = funnel.sender_id;
      this.funnels[edited_funnel].domain = funnel.domain;
      this.funnels[edited_funnel].show_in_marketplace = funnel.show_in_marketplace;

      this.funnel = null;
    },
  },
};
</script>
<style lang="scss">
.bg-c-red {
  background-color: #fbe8d7;
  color: #ee9b5d;
}
.bg-c-green {
  background-color: #cfecdb;
  color: #43b06f;
}
.scroll-modal-height {
  max-height: calc(100vh - 100px);
}
.custom-radio {
  .border.p-2 {
    min-height: 65px;
    margin: 0;
    cursor: pointer;
  }
  .w-6 {
    height: 22px;
    margin: 0;
  }
  label {
    cursor: pointer;
  }
  .text-grey-500 {
    font-size: 14px;
  }
}

.gs-funnels-page {
  color: #171b3f;
}

.gs-funnels-page__content {
  width: min(1280px, calc(100vw - 48px));
  margin: 0 auto;
}

.gs-funnels-page__create {
  box-shadow: 0 10px 22px rgba(255, 91, 127, .24);
}

.gs-funnels-page__state {
  max-width: 560px;
  margin: 72px auto;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(31, 37, 72, .08);
}

.gs-funnels-page__state h1 {
  margin: 0;
  color: #171b3f;
  font-size: 24px !important;
}

.gs-funnels-page__state p {
  margin: 12px 0 24px;
  color: #7c849c;
  line-height: 1.6;
}

.gs-funnels-page__warning {
  margin: 18px 0 0;
  padding: 12px 14px;
  color: #805800;
  font-size: 14px;
  background: #fff8df;
  border: 1px solid #f2dda0;
  border-radius: 8px;
}

@media (max-width: 700px) {
  .gs-funnels-page__content {
    width: calc(100vw - 28px);
  }
}
</style>
